/*
 * FOSCO Native Product Lightbox
 *
 * Features:
 * - Opens only when the main product image is clicked
 * - Leaves Warehouse thumbnail behavior unchanged
 * - Previous/next arrows
 * - Infinite looping
 * - Keyboard navigation
 * - Click left/right half of image to navigate
 * - Mobile swipe
 * - Image preloading
 * - Fade transitions
 * - Escape and backdrop close
 */

(function () {
  'use strict';

  function initializeFoscoLightbox() {
    const lightbox = document.getElementById('FoscoProductLightbox');

    if (!lightbox || lightbox.dataset.initialized === 'true') {
      return;
    }

    lightbox.dataset.initialized = 'true';

    const lightboxImage = lightbox.querySelector(
      '.fosco-lightbox__image'
    );

    const stage = lightbox.querySelector(
      '.fosco-lightbox__stage'
    );

    const closeButton = lightbox.querySelector(
      '.fosco-lightbox__close'
    );

    const previousButton = lightbox.querySelector(
      '.fosco-lightbox__arrow--previous'
    );

    const nextButton = lightbox.querySelector(
      '.fosco-lightbox__arrow--next'
    );

    const counter = lightbox.querySelector(
      '.fosco-lightbox__counter'
    );

    if (
      !lightboxImage ||
      !stage ||
      !closeButton ||
      !previousButton ||
      !nextButton ||
      !counter
    ) {
      return;
    }

    /*
     * These selectors are deliberately broad because Warehouse versions
     * can use slightly different gallery markup.
     */
    const galleryRootSelector = [
      '.product-gallery',
      '[data-product-gallery]',
      '.product__media-wrapper',
      '.product-media',
      '.product-gallery__wrapper'
    ].join(', ');

    /*
     * Clicks inside these areas remain controlled by Warehouse.
     */
    const thumbnailSelector = [
      '.product-gallery__thumbnail',
      '.product-gallery__thumbnail-list',
      '.product-gallery__thumbnail-scroller',
      '.thumbnail-list',
      '.product__media-thumbnail',
      '[data-action="select-media"]',
      '[data-thumbnail]',
      'button[aria-label*="thumbnail" i]'
    ].join(', ');

    /*
     * Used to discover every available product image, including thumbnails.
     */
    const galleryImageSelector = [
      '.product-gallery img',
      '[data-product-gallery] img',
      '.product__media-wrapper img',
      '.product-media img',
      '.product-gallery__wrapper img'
    ].join(', ');

    let galleryImages = [];
    let currentIndex = 0;
    let lastFocusedElement = null;
    let imageChangeTimer = null;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    const swipeThreshold = 45;

    function absoluteUrl(url) {
      if (!url) {
        return '';
      }

      try {
        return new URL(url, window.location.origin).href;
      } catch (error) {
        return url;
      }
    }

    function normalizeImageUrl(url) {
  if (!url) {
    return '';
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);

    /*
     * Remove all query parameters because Shopify often gives the same
     * product image different width/version URLs for thumbnails and main images.
     */
    let pathname = decodeURIComponent(parsedUrl.pathname);

    /*
     * Normalize protocol-relative and Shopify CDN variations.
     */
    pathname = pathname
      .replace(/_[0-9]+x[0-9]+(?=\.[a-z]+$)/i, '')
      .replace(/_[0-9]+x(?=\.[a-z]+$)/i, '')
      .replace(/_x[0-9]+(?=\.[a-z]+$)/i, '')
      .replace(/_(small|compact|medium|large|grande|master)(?=\.[a-z]+$)/i, '');

    return pathname.toLowerCase();
  } catch (error) {
    return String(url)
      .split('?')[0]
      .replace(/_[0-9]+x[0-9]+(?=\.[a-z]+$)/i, '')
      .replace(/_[0-9]+x(?=\.[a-z]+$)/i, '')
      .replace(/_x[0-9]+(?=\.[a-z]+$)/i, '')
      .replace(/_(small|compact|medium|large|grande|master)(?=\.[a-z]+$)/i, '')
      .toLowerCase();
  }
}

    function getLargestSrcsetUrl(image) {
      const srcset = image.getAttribute('srcset');

      if (!srcset) {
        return '';
      }

      const candidates = srcset
        .split(',')
        .map(function (candidate) {
          const parts = candidate.trim().split(/\s+/);

          return {
            url: parts[0] || '',
            width: parseInt(parts[1], 10) || 0
          };
        })
        .filter(function (candidate) {
          return candidate.url;
        })
        .sort(function (first, second) {
          return second.width - first.width;
        });

      if (!candidates.length) {
        return '';
      }

      return absoluteUrl(candidates[0].url);
    }

    function getImageUrl(image) {
      if (!image) {
        return '';
      }

      /*
       * Prefer an original or zoom source supplied by Warehouse.
       */
      const dataUrl =
        image.getAttribute('data-zoom') ||
        image.getAttribute('data-zoom-image') ||
        image.getAttribute('data-original-src') ||
        image.getAttribute('data-src');

      if (dataUrl) {
        return absoluteUrl(dataUrl);
      }

      /*
       * Some gallery images link directly to the original image.
       */
      const parentLink = image.closest('a[href]');

      if (parentLink) {
        const href = parentLink.getAttribute('href');

        if (
          href &&
          (
            href.includes('/cdn/shop/') ||
            /\.(jpg|jpeg|png|webp|gif|avif)(\?|$)/i.test(href)
          )
        ) {
          return absoluteUrl(href);
        }
      }

      /*
       * Otherwise use the largest responsive image candidate.
       */
      const largestSrcsetUrl = getLargestSrcsetUrl(image);

      if (largestSrcsetUrl) {
        return largestSrcsetUrl;
      }

      if (image.currentSrc) {
        return absoluteUrl(image.currentSrc);
      }

      return absoluteUrl(image.src || '');
    }

    function isUsableProductImage(image) {
      if (!image) {
        return false;
      }

      const imageUrl = getImageUrl(image);

      if (!imageUrl) {
        return false;
      }

      const imageClass = image.getAttribute('class') || '';

      if (/icon|logo|spinner|placeholder/i.test(imageClass)) {
        return false;
      }

      return true;
    }

    function collectGalleryImages() {
      const foundImages = Array.from(
        document.querySelectorAll(galleryImageSelector)
      );

      const uniqueImages = [];
      const seenUrls = new Set();

      foundImages.forEach(function (image) {
        if (!isUsableProductImage(image)) {
          return;
        }

        const fullUrl = getImageUrl(image);
        const normalizedUrl = normalizeImageUrl(fullUrl);

        if (!normalizedUrl || seenUrls.has(normalizedUrl)) {
          return;
        }

        seenUrls.add(normalizedUrl);

        uniqueImages.push({
          url: fullUrl,
          normalizedUrl: normalizedUrl,
          alt: image.alt || 'Product image'
        });
      });

      return uniqueImages;
    }

    function preloadImage(index) {
      if (!galleryImages.length) {
        return;
      }

      const normalizedIndex =
        (index + galleryImages.length) % galleryImages.length;

      const imageToPreload = new Image();
      imageToPreload.src = galleryImages[normalizedIndex].url;
    }

    function preloadAdjacentImages() {
      if (galleryImages.length < 2) {
        return;
      }

      preloadImage(currentIndex - 1);
      preloadImage(currentIndex + 1);
    }

    function updateControls() {
      const hasMultipleImages = galleryImages.length > 1;

      previousButton.hidden = !hasMultipleImages;
      nextButton.hidden = !hasMultipleImages;
      counter.hidden = galleryImages.length === 0;

      if (galleryImages.length) {
        counter.textContent =
          String(currentIndex + 1) +
          ' / ' +
          String(galleryImages.length);
      }
    }

    function applySelectedImage() {
      if (!galleryImages.length) {
        return;
      }

      const selectedImage = galleryImages[currentIndex];

      lightboxImage.src = selectedImage.url;
      lightboxImage.alt = selectedImage.alt;

      updateControls();
      preloadAdjacentImages();

      requestAnimationFrame(function () {
        lightboxImage.classList.remove('is-changing');
      });
    }

    function showImage(index, animate) {
      if (!galleryImages.length) {
        return;
      }

      currentIndex =
        (index + galleryImages.length) % galleryImages.length;

      if (imageChangeTimer) {
        window.clearTimeout(imageChangeTimer);
      }

      if (animate === false) {
        applySelectedImage();
        return;
      }

      lightboxImage.classList.add('is-changing');

      imageChangeTimer = window.setTimeout(function () {
        applySelectedImage();
      }, 90);
    }

    function showPreviousImage() {
      showImage(currentIndex - 1, true);
    }

    function showNextImage() {
      showImage(currentIndex + 1, true);
    }

    function lockPageScroll() {
      document.documentElement.dataset.foscoPreviousOverflow =
        document.documentElement.style.overflow || '';

      document.body.dataset.foscoPreviousOverflow =
        document.body.style.overflow || '';

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    function restorePageScroll() {
      document.documentElement.style.overflow =
        document.documentElement.dataset.foscoPreviousOverflow || '';

      document.body.style.overflow =
        document.body.dataset.foscoPreviousOverflow || '';

      delete document.documentElement.dataset.foscoPreviousOverflow;
      delete document.body.dataset.foscoPreviousOverflow;
    }

    function openLightbox(clickedImage) {
      galleryImages = collectGalleryImages();

      if (!galleryImages.length) {
        return;
      }

      const clickedUrl = normalizeImageUrl(
        getImageUrl(clickedImage)
      );

      const matchingIndex = galleryImages.findIndex(
        function (galleryImage) {
          return galleryImage.normalizedUrl === clickedUrl;
        }
      );

      currentIndex = matchingIndex >= 0 ? matchingIndex : 0;
      lastFocusedElement = document.activeElement;

      showImage(currentIndex, false);

      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');

      lockPageScroll();

      window.setTimeout(function () {
        closeButton.focus();
      }, 20);
    }

    function closeLightbox() {
      if (!lightbox.classList.contains('is-open')) {
        return;
      }

      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');

      restorePageScroll();

      window.setTimeout(function () {
        lightboxImage.src = '';
        galleryImages = [];
      }, 190);

      if (
        lastFocusedElement &&
        typeof lastFocusedElement.focus === 'function'
      ) {
        lastFocusedElement.focus();
      }
    }

    /*
     * Detect clicks on the Warehouse main product image.
     *
     * The displayed-size check excludes the small thumbnails while avoiding
     * dependence on a particular Warehouse version's class names.
     */
    document.addEventListener(
      'click',
      function (event) {
        if (lightbox.classList.contains('is-open')) {
          return;
        }

        const clickedImage = event.target.closest('img');

        if (!clickedImage) {
          return;
        }

        const galleryRoot = clickedImage.closest(
          galleryRootSelector
        );

        if (!galleryRoot) {
          return;
        }

        if (clickedImage.closest(thumbnailSelector)) {
          return;
        }

        const imageRect = clickedImage.getBoundingClientRect();

        if (imageRect.width < 120 && imageRect.height < 120) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        openLightbox(clickedImage);
      },
      true
    );

    closeButton.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      closeLightbox();
    });

    previousButton.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      showPreviousImage();
    });

    nextButton.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      showNextImage();
    });

    /*
     * Clicking the left or right half of the enlarged image navigates.
     */
    lightboxImage.addEventListener('click', function (event) {
      if (galleryImages.length < 2) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const imageRect = lightboxImage.getBoundingClientRect();
      const clickPosition = event.clientX - imageRect.left;

      if (clickPosition < imageRect.width / 2) {
        showPreviousImage();
      } else {
        showNextImage();
      }
    });

    /*
     * Clicking outside the image closes the lightbox.
     */
    lightbox.addEventListener('click', function (event) {
      if (
        event.target === lightbox ||
        event.target === stage
      ) {
        closeLightbox();
      }
    });

    /*
     * Keyboard support.
     */
    document.addEventListener('keydown', function (event) {
      if (!lightbox.classList.contains('is-open')) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeLightbox();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPreviousImage();
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNextImage();
        return;
      }

      /*
       * Keep keyboard focus inside the open dialog.
       */
      if (event.key === 'Tab') {
        const focusableElements = [
          closeButton,
          previousButton,
          nextButton
        ].filter(function (element) {
          return !element.hidden;
        });

        if (!focusableElements.length) {
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement =
          focusableElements[focusableElements.length - 1];

        if (
          event.shiftKey &&
          document.activeElement === firstElement
        ) {
          event.preventDefault();
          lastElement.focus();
        } else if (
          !event.shiftKey &&
          document.activeElement === lastElement
        ) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    });

    /*
     * Mobile swipe support.
     */
    stage.addEventListener(
      'touchstart',
      function (event) {
        if (event.touches.length !== 1) {
          return;
        }

        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        touchEndX = touchStartX;
        touchEndY = touchStartY;
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchmove',
      function (event) {
        if (event.touches.length !== 1) {
          return;
        }

        touchEndX = event.touches[0].clientX;
        touchEndY = event.touches[0].clientY;
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      function () {
        if (galleryImages.length < 2) {
          return;
        }

        const horizontalDistance = touchEndX - touchStartX;
        const verticalDistance = touchEndY - touchStartY;

        /*
         * Ignore vertical gestures so normal mobile behavior remains natural.
         */
        if (
          Math.abs(horizontalDistance) < swipeThreshold ||
          Math.abs(horizontalDistance) <= Math.abs(verticalDistance)
        ) {
          return;
        }

        if (horizontalDistance > 0) {
          showPreviousImage();
        } else {
          showNextImage();
        }
      },
      { passive: true }
    );

    /*
     * Reinitialize correctly when Shopify's theme editor reloads sections.
     */
    document.addEventListener(
      'shopify:section:load',
      function () {
        galleryImages = [];
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeFoscoLightbox
    );
  } else {
    initializeFoscoLightbox();
  }
})();